import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  Event as EventIcon,
  Checkroom as CheckroomIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  DirectionsCar as DirectionsCarIcon
} from '@mui/icons-material';

interface ChangeRequest {
  id: string;
  guestId: string;
  requestType: 'event_change' | 'size_change' | 'transportation';
  currentValue: any;
  requestedValue: any;
  reason?: string;
  status: 'pending' | 'approved' | 'denied';
  adminNotes?: string;
  createdAt: string;
  reviewedAt?: string;
}

interface GuestRequestManagerProps {
  pendingRequests: ChangeRequest[];
  requestHistory: ChangeRequest[];
  onRefresh: () => void;
}

export const GuestRequestManager: React.FC<GuestRequestManagerProps> = ({ 
  pendingRequests = [], 
  requestHistory = [], 
  onRefresh 
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <PendingIcon color="warning" />;
      case 'approved':
        return <CheckCircleIcon color="success" />;
      case 'denied':
        return <CancelIcon color="error" />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'denied':
        return 'error';
      default:
        return 'default';
    }
  };

  const getRequestTypeIcon = (type: string) => {
    switch (type) {
      case 'event_change':
        return <EventIcon />;
      case 'size_change':
        return <CheckroomIcon />;
      case 'transportation':
        return <DirectionsCarIcon />;
      default:
        return <AssignmentIcon />;
    }
  };

  const getRequestTypeLabel = (type: string) => {
    switch (type) {
      case 'event_change':
        return 'Event Change';
      case 'size_change':
        return 'Size Change';
      case 'transportation':
        return 'Transportation Request';
      default:
        return 'Unknown';
    }
  };

  const renderEventChangeDetails = (request: ChangeRequest) => {
    const current = request.currentValue;
    const requested = request.requestedValue;
    
    if (!requested) {
      return (
        <Typography variant="body2" color="text.secondary">
          Event change details not available
        </Typography>
      );
    }
    
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Event Change Request
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Action: {requested.action === 'register' ? 'Register for event' : 'Unregister from event'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Event ID: {requested.eventId || 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderSizeChangeDetails = (request: ChangeRequest) => {
    const current = request.currentValue;
    const requested = request.requestedValue;
    
    if (!requested || !current) {
      return (
        <Typography variant="body2" color="text.secondary">
          Size change details not available
        </Typography>
      );
    }
    
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Size Change Request
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Current Size: {current.size || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Requested Size: {requested.newSize || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Item: {current.itemType || 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderTransportationDetails = (request: ChangeRequest) => {
    const requested = request.requestedValue;
    
    if (!requested) {
      return (
        <Typography variant="body2" color="text.secondary">
          Transportation details not available
        </Typography>
      );
    }
    
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Transportation Request
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Direction: {requested.direction === 'to_event' ? 'To Event' : 'From Event'}
            </Typography>
          </Grid>
          {requested.flightNumber && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Flight: {requested.airline || ''} {requested.flightNumber}
              </Typography>
            </Grid>
          )}
          {requested.arrivalTime && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Arrival: {new Date(requested.arrivalTime).toLocaleString()}
              </Typography>
            </Grid>
          )}
          {requested.departureTime && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Departure: {new Date(requested.departureTime).toLocaleString()}
              </Typography>
            </Grid>
          )}
          {requested.specialRequests && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Special Requests: {requested.specialRequests}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  const renderRequestCard = (request: ChangeRequest) => (
    <Card key={request.id} sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center">
            {getRequestTypeIcon(request.requestType)}
            <Typography variant="h6" sx={{ ml: 1 }}>
              {getRequestTypeLabel(request.requestType)}
            </Typography>
          </Box>
          <Chip
            icon={getStatusIcon(request.status)}
            label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
            color={getStatusColor(request.status) as any}
            size="small"
          />
        </Box>

        {request.requestType === 'event_change' && renderEventChangeDetails(request)}
        {request.requestType === 'size_change' && renderSizeChangeDetails(request)}
        {request.requestType === 'transportation' && renderTransportationDetails(request)}

        {request.reason && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="medium">
              Reason:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {request.reason}
            </Typography>
          </Box>
        )}

        <Box display="flex" alignItems="center" mt={2}>
          <ScheduleIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Submitted: {formatDate(request.createdAt)}
          </Typography>
        </Box>

        {request.reviewedAt && (
          <Box display="flex" alignItems="center" mt={1}>
            <ScheduleIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Reviewed: {formatDate(request.reviewedAt)}
            </Typography>
          </Box>
        )}

        {request.adminNotes && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="medium">
              Admin Notes:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {request.adminNotes}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const allRequests = [...(pendingRequests || []), ...(requestHistory || [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AssignmentIcon sx={{ mr: 1 }} />
        My Requests
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab 
            label={`Pending (${pendingRequests.length})`}
            icon={<PendingIcon />}
            iconPosition="start"
          />
          <Tab 
            label={`History (${requestHistory.length})`}
            icon={<AssignmentIcon />}
            iconPosition="start"
          />
          <Tab 
            label={`All (${allRequests.length})`}
            icon={<AssignmentIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Pending Requests Tab */}
      {activeTab === 0 && (
        <Box>
          {pendingRequests.length === 0 ? (
            <Alert severity="info">
              No pending requests. All your requests have been processed.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You have {pendingRequests.length} pending request{pendingRequests.length !== 1 ? 's' : ''} awaiting review.
              </Typography>
              {pendingRequests.map(renderRequestCard)}
            </Box>
          )}
        </Box>
      )}

      {/* Request History Tab */}
      {activeTab === 1 && (
        <Box>
          {requestHistory.length === 0 ? (
            <Alert severity="info">
              No request history found.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Showing {requestHistory.length} processed request{requestHistory.length !== 1 ? 's' : ''}.
              </Typography>
              {requestHistory.map(renderRequestCard)}
            </Box>
          )}
        </Box>
      )}

      {/* All Requests Tab */}
      {activeTab === 2 && (
        <Box>
          {allRequests.length === 0 ? (
            <Alert severity="info">
              No requests found. You can submit requests from the Events or Clothing sections.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Showing all {allRequests.length} request{allRequests.length !== 1 ? 's' : ''}.
              </Typography>
              {allRequests.map(renderRequestCard)}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};